// 공통
import { IGlobalOrderCompleteClientLogs } from '~/api/converters/global/order-complete/ClientLogs/index.type'

export interface IBrazeCommonLog {
  // 국내숙소 (모텔/호텔/펜션/게스트하우스), 국내레저, 국내교통(고속버스, 국내기차), 해외숙소 카테고리 한글명
  property_category?: string
  // 국내숙소 property_id, 레저 deal_id, 교통 출&도착지코드, 해외숙소 ID
  item_id?: string | number
  // 국내숙소명 / 해외숙소명 / 레저 상품명 / 교통 출&도착지명(편도, 왕복은 순서대로 기재)
  item_name?: string
  // 국내숙소 체크인, 레저 사용 시작일, 교통 탑승일(편도/왕복-가는편), 해외숙소 체크인
  check_in_date?: string
  // 국내/해외 숙소 체크아웃, 레저 사용 마감일, 교통 탑승일(왕복-돌아오는 편)
  check_out_date?: string
  // 국내 숙소 대실/숙박/연박
  use_type?: string
  // 주문금액 (판매가기준, 숙박 1박당 가격, 레저 티켓 단가 unit price)
  reserve_price?: number
  // 실결제금액
  paid_amount?: number
}

// 장바구니 진입
export interface IBrazeClickCartIntroLog extends IBrazeCommonLog {
  // 장바구니 ID
  cart_id: string
  // 장바구니에 담긴 카테고리 distinct (숙소/레저/교통)
  cart_category: string
  // 장바구니에 담긴 카테고리 갯수 distinct (1~3)
  cart_category_cnt: number
  // 장바구니에 담긴 합계 금액
  cart_amount: number
}

// 주문페이지 진입
export interface IBrazeClickOrderIntro extends IBrazeCommonLog {
  // 국내숙소 (모텔/호텔/펜션/게스트하우스), 국내레저, 국내교통(고속버스, 국내기차), 해외숙소 카테고리 한글명
  category: string
  // 국내기차의 하위 카테고리 (KTX, 새마을호...)
  subcategory?: string
  // 주문금액 (판매가기준, 숙박 가격, 레저 티켓 단가 unit price)
  reserve_price: number
  // 도시명
  city_name: string
}

// 주문 완료 페이지 진입
export interface IBrazeCompositeOrderComplete extends IBrazeCommonLog {
  // 국내숙소 (모텔/호텔/펜션/게스트하우스), 국내레저, 국내교통(고속버스, 국내기차), 해외숙소 카테고리 한글명
  category: Category
  // 국내기차의 하위 카테고리 (KTX, 새마을호...)
  subcategory?: string
  // 주문 아이디 (통합 주문 번호)
  composite_order_id: string
  // 대실 포함 여부 (대실 주문 시 true/대실 미포함 시 false)
  dayuse_included?: boolean
  // 미리예약 포함 여부 (당일 예약 시 false)
  is_earlybird: boolean
  // 국내/해외숙소 roomTypeId 국내 레저 optionID
  item_detail_id?: string
  // 국내/해외숙소 roomTypeName 국내 레저 optionName
  item_detail_name?: string
  /**
   * 이용인원 카운트 (구매한 갯수로 추정)
   * 국내숙소 : adults
   * 해외숙소 : 투숙인원..
   * 국내 레저 : items 의 수
   * 기차/버스 : tickets 의 수
   */
  cust_count: number
  // 쿠폰아이디
  CouponID?: string
  // 쿠폰 사용금액
  CouponAmount: number
  // 포인트 사용금액
  PointAmount: number
  // 국내숙소 방문 타입 (도보/차량/자가/대중교통)
  visitType?: BrazeVisitType
  /**
   * 키즈 타겟을 포함하고 있는지 True/False
   * 국내숙소 예약 childrenAges not null
   * 국내 기차 예약 child > 0
   * 국내 버스 ticketType = '2' (초등학생)
   */
  has_children?: boolean
  // 장바구니를 통해 주문한 distinct 카테고리
  cart_category: string
  // 장바구니를 통해 주문한 distinct 카테고리 갯수
  cart_category_cnt: number
  // 장바구니 패키지 할인 금액
  cart_package_discount: number
  // 놀카드 승인 여부
  nolcard_approval: boolean
  // 도시명
  city_name: string
  // 주문 이전 페이지가 장바구니인지 여부
  cart_order_yn: boolean
}

// Braze 구매 이벤트 내의 custom property
interface IPurchaseProperties {
  [key: string]: any
  // 국내숙소 카테고리 (모텔/호텔/펜션/게스트하우스), 국내레저, 국내교통, 해외숙소
  category: Category
  subcategory?: string
  // 결제수단 코드 수집
  payment_method: Array<string>
  // 실결제금액
  paid_amount: number
}

// Braze 구매 이벤트 전송 로그
export interface IBrazePurchaseEventLog {
  // 국내숙소 property_id, 레저 deal_id, 교통 출&도착지코드, 해외숙소 ID
  productId: string
  // 판매가
  price: number
  // KRW
  currencyCode: string
  // 구매수량
  quantity?: number
  purchaseProperties?: IPurchaseProperties
}

export enum Currency {
  KRW = 'KRW',
}

export enum Category {
  MOTEL = '모텔',
  HOTEL = '호텔',
  PENSION = '펜션',
  GUESTHOUSE = '게스트하우스',
  CAMPING = '캠핑',
  TRAIN = '국내기차',
  BUS = '고속버스',
  GIFTCARD = '모바일교환권',
  LEISURE = '레저',
  GLOBAL = '해외숙소',
}

export enum BrazeVisitType {
  WALK = '도보',
  VEHICLE = '차량',
  OWN_VEHICLE = '자가',
  PUBLIC_TRANSPORT = '대중교통',
  PARKING_AVAILABLE = '주차가능',
  PARKING_UNAVAILABLE = '주차불가',
}

/**
 * braze에서 사용하는 커스텀 이벤트 로그 타입
 * @link {Sheet|https://docs.google.com/spreadsheets/d/1iJdrTz1HPMxbPx7Au9SSv6S6DZas32ZbnFzEy0WtbMk/edit#gid=1308376500}
 * @link {Confluence|https://yanolja.atlassian.net/wiki/spaces/FES/pages/103755832/Braze+platform-site}
 */
export type IBrazeCustomEventLog =
  | IBrazeClickCartIntroLog
  | IBrazeClickOrderIntro
  | IBrazeCompositeOrderComplete
  | IGlobalOrderCompleteClientLogs['braze']
